import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, X, RefreshCw, AlertCircle } from 'lucide-react';

const SimpleAdmin = () => {
  const [products, setProducts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [backendMode, setBackendMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'daily',
    price: '',
    originalPrice: '',
    rating: '',
    reviews: '',
    waterContent: '',
    material: '',
    image: '',
    prescription: false,
    inStock: true,
    bestseller: false,
    colors: ''
  });

  // Backend configuration
  const API_BASE_URL = process.env.NODE_ENV === 'production' 
    ? 'https://yourdomain.com/api/api'  // Updated path for production
    : 'http://localhost:8080/api';     // Local PHP server
  
  const API_KEY = 'nova_admin_2025_key'; // Your admin API key

  // Test backend connection and load products
  useEffect(() => {
    testBackendConnection();
  }, []);

  const testBackendConnection = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/health`, {
        headers: {
          'X-API-Key': API_KEY,
        },
      });
      
      if (response.ok) {
        setBackendMode(true);
        loadProductsFromBackend();
      } else {
        throw new Error('Backend not available');
      }
    } catch (err) {
      console.log('Backend not available, using localStorage mode');
      setBackendMode(false);
      loadProductsFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  const loadProductsFromBackend = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin?action=products`, {
        headers: {
          'X-API-Key': API_KEY,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      
      const data = await response.json();
      if (data.success) {
        setProducts(data.data);
        setSuccess('Products loaded from database');
      } else {
        throw new Error(data.error || 'Failed to load products');
      }
    } catch (err) {
      setError(`Error loading products: ${err.message}`);
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProductsFromLocalStorage = () => {
    const savedProducts = localStorage.getItem('nova_products');
    if (savedProducts) {
      setProducts(JSON.parse(savedProducts));
    } else {
      // Initialize with sample data
      const sampleProducts = [
        {
          id: 1,
          name: 'Nova SN4001',
          category: 'daily',
          price: 29.99,
          originalPrice: 39.99,
          rating: 4.8,
          reviews: 124,
          waterContent: '42%',
          material: 'HEMA',
          image: '/images/nova-sn4001.jpg',
          prescription: true,
          inStock: true,
          bestseller: true,
          colors: ['Clear', 'Blue', 'Green']
        },
        {
          id: 2,
          name: 'Nova SN4002',
          category: 'weekly',
          price: 24.99,
          originalPrice: null,
          rating: 4.7,
          reviews: 89,
          waterContent: '38%',
          material: 'HEMA',
          image: '/images/nova-sn4002.jpg',
          prescription: false,
          inStock: true,
          bestseller: false,
          colors: ['Clear', 'Hazel', 'Gray']
        }
      ];
      setProducts(sampleProducts);
      localStorage.setItem('nova_products', JSON.stringify(sampleProducts));
    }
  };

  // Save products to localStorage whenever products change (localStorage mode only)
  useEffect(() => {
    if (!backendMode && products.length > 0) {
      localStorage.setItem('nova_products', JSON.stringify(products));
    }
  }, [products, backendMode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      original_price: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
      rating: parseFloat(formData.rating),
      reviews: parseInt(formData.reviews),
      colors: formData.colors.split(',').map(c => c.trim()).filter(c => c),
      prescription: formData.prescription,
      in_stock: formData.inStock,
      bestseller: formData.bestseller
    };

    try {
      if (backendMode) {
        await handleBackendSubmit(productData);
      } else {
        handleLocalStorageSubmit(productData);
      }
      
      resetForm();
      setSuccess(editingProduct ? 'Product updated successfully!' : 'Product added successfully!');
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBackendSubmit = async (productData) => {
    const url = editingProduct 
      ? `${API_BASE_URL}/admin?id=${editingProduct.id}`
      : `${API_BASE_URL}/admin`;
    
    const method = editingProduct ? 'PUT' : 'POST';
    
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify(productData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save product');
    }
    
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to save product');
    }
    
    // Reload products from backend
    await loadProductsFromBackend();
  };

  const handleLocalStorageSubmit = (productData) => {
    const newProduct = {
      ...productData,
      id: editingProduct ? editingProduct.id : Date.now(),
      originalPrice: productData.original_price, // Convert back for localStorage
      colors: productData.colors
    };

    if (editingProduct) {
      setProducts(prev => prev.map(p => p.id === editingProduct.id ? newProduct : p));
      setEditingProduct(null);
    } else {
      setProducts(prev => [...prev, newProduct]);
      setShowAddForm(false);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      ...product,
      originalPrice: product.originalPrice || product.original_price || '',
      inStock: product.inStock || product.in_stock,
      colors: Array.isArray(product.colors) ? product.colors.join(', ') : product.colors || ''
    });
    setEditingProduct(product);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (backendMode) {
        const response = await fetch(`${API_BASE_URL}/admin?id=${id}`, {
          method: 'DELETE',
          headers: {
            'X-API-Key': API_KEY,
          },
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete product');
        }
        
        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Failed to delete product');
        }
        
        // Reload products from backend
        await loadProductsFromBackend();
      } else {
        setProducts(prev => prev.filter(p => p.id !== id));
      }
      
      setSuccess('Product deleted successfully!');
    } catch (err) {
      setError(`Error deleting product: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshProducts = () => {
    if (backendMode) {
      loadProductsFromBackend();
    } else {
      loadProductsFromLocalStorage();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'daily',
      price: '',
      originalPrice: '',
      rating: '',
      reviews: '',
      waterContent: '',
      material: '',
      image: '',
      prescription: false,
      inStock: true,
      bestseller: false,
      colors: ''
    });
    setEditingProduct(null);
    setShowAddForm(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '15px',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
          color: 'white',
          padding: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ fontSize: '2rem', margin: '0 0 0.5rem 0' }}>Nova Admin Panel</h1>
            <p style={{ margin: 0, opacity: 0.9 }}>
              {backendMode ? 'Connected to Database' : 'Local Storage Mode'}
              {loading && ' (Loading...)'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={refreshProducts}
              disabled={loading}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: loading ? 0.5 : 1
              }}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <div style={{
              background: backendMode ? '#27ae60' : '#f39c12',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}>
              {backendMode ? 'DB' : 'LOCAL'}
            </div>
            <Link to="/" style={{
              color: 'white',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              border: '2px solid rgba(255,255,255,0.3)',
              borderRadius: '8px'
            }}>
              <ArrowLeft size={20} />
              Back to Site
            </Link>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          {/* Error/Success Messages */}
          {error && (
            <div style={{
              background: '#f8d7da',
              color: '#721c24',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              border: '1px solid #f5c6cb',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <AlertCircle size={20} />
              {error}
            </div>
          )}
          
          {success && (
            <div style={{
              background: '#d4edda',
              color: '#155724',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              border: '1px solid #c3e6cb'
            }}>
              {success}
            </div>
          )}
          {/* Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: 'white',
              padding: '1.5rem',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{products.length}</div>
              <div>Total Products</div>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #55a3ff 0%, #3742fa 100%)',
              color: 'white',
              padding: '1.5rem',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                {products.filter(p => p.inStock).length}
              </div>
              <div>In Stock</div>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #ffa726 0%, #ff6f00 100%)',
              color: 'white',
              padding: '1.5rem',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                {products.filter(p => p.bestseller).length}
              </div>
              <div>Bestsellers</div>
            </div>
          </div>

          {/* Add Product Button */}
          {!showAddForm && (
            <div style={{ marginBottom: '2rem' }}>
              <button
                onClick={() => setShowAddForm(true)}
                style={{
                  background: 'linear-gradient(135deg, #27ae60 0%, #219a52 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                <Plus size={20} />
                Add New Product
              </button>
            </div>
          )}

          {/* Add/Edit Form */}
          {showAddForm && (
            <div style={{
              background: '#f8f9fa',
              padding: '2rem',
              borderRadius: '12px',
              marginBottom: '2rem',
              border: '2px solid #e1e8ed'
            }}>
              <h3 style={{ marginTop: 0 }}>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Product Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #e1e8ed',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #e1e8ed',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  <input
                    type="number"
                    name="price"
                    placeholder="Price"
                    value={formData.price}
                    onChange={handleInputChange}
                    step="0.01"
                    required
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #e1e8ed',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <input
                    type="number"
                    name="originalPrice"
                    placeholder="Original Price (optional)"
                    value={formData.originalPrice}
                    onChange={handleInputChange}
                    step="0.01"
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #e1e8ed',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <input
                    type="number"
                    name="rating"
                    placeholder="Rating (0-5)"
                    value={formData.rating}
                    onChange={handleInputChange}
                    step="0.1"
                    min="0"
                    max="5"
                    required
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #e1e8ed',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <input
                    type="number"
                    name="reviews"
                    placeholder="Number of Reviews"
                    value={formData.reviews}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #e1e8ed',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <input
                    type="text"
                    name="waterContent"
                    placeholder="Water Content (e.g., 42%)"
                    value={formData.waterContent}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #e1e8ed',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <input
                    type="text"
                    name="material"
                    placeholder="Material (e.g., HEMA)"
                    value={formData.material}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #e1e8ed',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                
                <input
                  type="text"
                  name="image"
                  placeholder="Image URL"
                  value={formData.image}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e1e8ed',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    marginBottom: '1rem'
                  }}
                />
                
                <input
                  type="text"
                  name="colors"
                  placeholder="Colors (comma-separated, e.g., Clear, Blue, Green)"
                  value={formData.colors}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e1e8ed',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    marginBottom: '1rem'
                  }}
                />

                <div style={{
                  display: 'flex',
                  gap: '2rem',
                  marginBottom: '1rem'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      name="prescription"
                      checked={formData.prescription}
                      onChange={handleInputChange}
                    />
                    Requires Prescription
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      name="inStock"
                      checked={formData.inStock}
                      onChange={handleInputChange}
                    />
                    In Stock
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      name="bestseller"
                      checked={formData.bestseller}
                      onChange={handleInputChange}
                    />
                    Bestseller
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      background: loading 
                        ? 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)'
                        : 'linear-gradient(135deg, #27ae60 0%, #219a52 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '1rem',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    <Save size={16} />
                    {loading 
                      ? 'Saving...' 
                      : (editingProduct ? 'Update Product' : 'Add Product')
                    }
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={loading}
                    style={{
                      background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '1rem',
                      opacity: loading ? 0.5 : 1
                    }}
                  >
                    <X size={16} />
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Products List */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1rem'
          }}>
            {products.map(product => (
              <div key={product.id} style={{
                border: '2px solid #e1e8ed',
                borderRadius: '12px',
                padding: '1.5rem',
                background: '#f8f9fa'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>{product.name}</h3>
                  <span style={{
                    background: '#3498db',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }}>
                    {product.category}
                  </span>
                </div>
                
                <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Price:</strong> ${product.price}
                    {(product.originalPrice || product.original_price) && (
                      <span style={{ textDecoration: 'line-through', marginLeft: '0.5rem', color: '#999' }}>
                        ${product.originalPrice || product.original_price}
                      </span>
                    )}
                  </p>
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Rating:</strong> ⭐ {product.rating} ({product.reviews} reviews)
                  </p>
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Water Content:</strong> {product.waterContent || product.water_content}
                  </p>
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Material:</strong> {product.material}
                  </p>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                  {product.prescription && (
                    <span style={{
                      background: '#e74c3c',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '6px',
                      fontSize: '0.75rem'
                    }}>
                      Prescription
                    </span>
                  )}
                  {product.bestseller && (
                    <span style={{
                      background: '#f39c12',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '6px',
                      fontSize: '0.75rem'
                    }}>
                      Bestseller
                    </span>
                  )}
                  <span style={{
                    background: (product.inStock || product.in_stock) ? '#27ae60' : '#95a5a6',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem'
                  }}>
                    {(product.inStock || product.in_stock) ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>

                {product.colors && product.colors.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ fontSize: '0.9rem' }}>Colors:</strong>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                      {product.colors.map((color, index) => (
                        <span key={index} style={{
                          background: '#ecf0f1',
                          padding: '0.125rem 0.375rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          color: '#2c3e50'
                        }}>
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleEdit(product)}
                    disabled={loading}
                    style={{
                      background: loading 
                        ? 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)'
                        : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.875rem',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    <Edit size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    disabled={loading}
                    style={{
                      background: loading 
                        ? 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)'
                        : 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.875rem',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {products.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              <h3>No products yet</h3>
              <p>Click "Add New Product" to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleAdmin;